<template>
  <el-dialog
    title="个人资料"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    :append-to-body="true"
    @close="handleClose"
  >
    <el-form
      ref="profileForm"
      :model="formData"
      :rules="rules"
      label-width="100px"
      v-loading="loading"
    >
      <!-- 头像 -->
      <el-form-item label="头像">
        <div class="avatar-section">
          <el-avatar
            :size="80"
            :src="formData.avatar"
            class="user-avatar"
          >
            <i class="el-icon-user-solid" style="font-size: 40px"></i>
          </el-avatar>
          <el-upload
            class="avatar-uploader"
            action="#"
            :show-file-list="false"
            :before-upload="beforeAvatarUpload"
            :http-request="uploadAvatar"
          >
            <el-button size="small" type="primary">更换头像</el-button>
          </el-upload>
        </div>
      </el-form-item>

      <!-- 用户名 (不可编辑) -->
      <el-form-item label="用户名">
        <el-input v-model="formData.username" disabled>
          <i slot="prefix" class="el-input__icon el-icon-user"></i>
        </el-input>
      </el-form-item>

      <!-- 昵称 -->
      <el-form-item label="昵称" prop="nickname">
        <el-input
          v-model="formData.nickname"
          placeholder="请输入昵称"
          maxlength="20"
          show-word-limit
        >
          <i slot="prefix" class="el-input__icon el-icon-edit"></i>
        </el-input>
      </el-form-item>

      <!-- 真实姓名 -->
      <el-form-item label="真实姓名" prop="real_name">
        <el-input
          v-model="formData.real_name"
          placeholder="请输入真实姓名"
          maxlength="20"
        >
          <i slot="prefix" class="el-input__icon el-icon-user-solid"></i>
        </el-input>
      </el-form-item>

      <!-- 邮箱 -->
      <el-form-item label="邮箱" prop="email">
        <el-input
          v-model="formData.email"
          placeholder="请输入邮箱地址"
          type="email"
        >
          <i slot="prefix" class="el-input__icon el-icon-message"></i>
        </el-input>
      </el-form-item>

      <!-- 手机号 -->
      <el-form-item label="手机号" prop="mobile">
        <el-input
          v-model="formData.mobile"
          placeholder="请输入手机号"
          maxlength="11"
        >
          <i slot="prefix" class="el-input__icon el-icon-phone"></i>
        </el-input>
      </el-form-item>

      <!-- 账号信息 (只读) -->
      <el-divider content-position="left">账号信息</el-divider>

      <el-form-item label="用户ID">
        <el-input v-model="formData.id" disabled>
          <i slot="prefix" class="el-input__icon el-icon-postcard"></i>
        </el-input>
      </el-form-item>

      <el-form-item label="角色">
        <el-tag :type="roleTagType">{{ roleText }}</el-tag>
      </el-form-item>

      <el-form-item label="注册时间">
        <el-input :value="registerDateText" disabled>
          <i slot="prefix" class="el-input__icon el-icon-time"></i>
        </el-input>
      </el-form-item>

      <!-- 修改密码按钮 -->
      <el-form-item label="密码">
        <el-button
          type="text"
          icon="el-icon-lock"
          @click="showChangePassword"
        >
          修改密码
        </el-button>
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">
        保 存
      </el-button>
    </span>

    <!-- 修改密码对话框 -->
    <change-password-dialog
      :visible.sync="changePasswordVisible"
    />
  </el-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ChangePasswordDialog from './ChangePasswordDialog.vue';

export default {
  name: 'ProfileDialog',
  components: {
    ChangePasswordDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      saving: false,
      changePasswordVisible: false,
      formData: {
        id: null,
        username: '',
        nickname: '',
        real_name: '',
        email: '',
        mobile: '',
        avatar: '',
        role: '',
        registerDate: null,
      },
      rules: {
        nickname: [
          { required: true, message: '请输入昵称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' },
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' },
        ],
        mobile: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' },
        ],
      },
    };
  },
  computed: {
    ...mapState('user', ['userInfo']),

    roleText() {
      const roleMap = {
        teacher: '家长',
        admin: '管理员',
        student: '学生',
        parent: '家长',
      };
      return roleMap[this.formData.role] || '未知';
    },

    roleTagType() {
      const typeMap = {
        teacher: 'success',
        admin: 'danger',
        student: '',
        parent: 'warning',
      };
      return typeMap[this.formData.role] || 'info';
    },

    registerDateText() {
      if (!this.formData.registerDate) return '-';
      const date = new Date(this.formData.registerDate * 1000);
      return date.toLocaleString('zh-CN');
    },
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.loadUserProfile();
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
    },
  },
  methods: {
    ...mapActions('user', ['updateProfile', 'getUserProfile']),

    /**
     * 加载用户资料
     */
    async loadUserProfile() {
      this.loading = true;
      try {
        // 从 store 获取最新用户信息
        await this.getUserProfile();
        
        // 复制到表单数据
        this.formData = {
          id: this.userInfo.id,
          username: this.userInfo.username,
          nickname: this.userInfo.nickname || this.userInfo.username,
          real_name: this.userInfo.real_name || '',
          email: this.userInfo.email || '',
          mobile: this.userInfo.mobile || '',
          avatar: this.userInfo.avatar || '',
          role: this.userInfo.role || 'teacher',
          registerDate: this.userInfo.createtime || this.userInfo.registerDate,
        };
      } catch (error) {
        console.error('加载用户资料失败:', error);
        this.$message.error('加载用户资料失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 保存用户资料
     */
    async handleSave() {
      // 表单验证
      try {
        await this.$refs.profileForm.validate();
      } catch (error) {
        return;
      }

      this.saving = true;
      try {
        // 只提交可编辑的字段
        const updateData = {
          nickname: this.formData.nickname,
          real_name: this.formData.real_name,
          email: this.formData.email,
          mobile: this.formData.mobile,
          avatar: this.formData.avatar,
        };

        const result = await this.updateProfile(updateData);
        
        if (result.success) {
          this.$message.success('保存成功');
          this.handleClose();
        } else {
          this.$message.error(result.message || '保存失败');
        }
      } catch (error) {
        console.error('保存用户资料失败:', error);
        this.$message.error('保存失败: ' + error.message);
      } finally {
        this.saving = false;
      }
    },

    /**
     * 关闭对话框
     */
    handleClose() {
      this.$refs.profileForm?.resetFields();
      this.dialogVisible = false;
    },

    /**
     * 显示修改密码对话框
     */
    showChangePassword() {
      this.changePasswordVisible = true;
    },

    /**
     * 头像上传前校验
     */
    beforeAvatarUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.$message.error('只能上传图片文件!');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!');
        return false;
      }
      return true;
    },

    /**
     * 自定义上传头像
     */
    async uploadAvatar(params) {
      const file = params.file;
      
      // 转换为 base64 (简单实现)
      const reader = new FileReader();
      reader.onload = (e) => {
        this.formData.avatar = e.target.result;
        this.$message.success('头像已更新，请点击保存');
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.avatar-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.user-avatar {
  background-color: #f5f7fa;
  color: #909399;
}

.avatar-uploader {
  flex: 1;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.el-divider {
  margin: 12px 0;
}

::v-deep .el-form-item__label {
  font-weight: 500;
}

::v-deep .el-input.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #909399;
}
</style>
